import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable, forkJoin, tap } from 'rxjs';
import { PerfilWeb } from '../cotizador-module/cotizador/automotor/interface/perfil_web';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private apiUrl: string = environment.apiPlataforma;
  private themeSubject = new BehaviorSubject<any>(null);
  theme$ = this.themeSubject.asObservable();

  private perfilSubject = new BehaviorSubject<PerfilWeb | null>(null);
  perfil$ = this.perfilSubject.asObservable();


  constructor(private http: HttpClient) {
    this.loadTheme();
  }




  obtenerPerfil(token: string): Observable<PerfilWeb> {

    const url = `${this.apiUrl}/api/v1/personalizacion/perfil_web`;
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}` });

    const dataToSend = {
      uuid: localStorage.getItem('uuid')
    }
    const options = { headers: headers };

    return this.http.post<PerfilWeb>(url, dataToSend, options).pipe(
      tap((perfil) => {
        this.perfilSubject.next(perfil); // Actualiza el BehaviorSubject
      })
    );

    //return this.http.post<PerfilWeb>(url, dataToSend, options);

  }

  /**
   * Obtiene las paletas de colores para primary y accent desde la API y las aplica al sistema.
   */
  getThemeColors(token: string, primaryScheme: string | undefined, accentScheme: string | undefined): Observable<any> {
    const url = `${this.apiUrl}/api/v1/personalizacion/obtener_colores`;
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}` });

    const dataToSendPrimary = { themes_id: primaryScheme };
    const dataToSendAccent = { themes_id: accentScheme };

    const options = { headers };

    // Hacemos dos solicitudes simultáneas utilizando forkJoin
    return forkJoin([
      this.http.post<{ [key: string]: { color: string; contrast: string } }>(url, dataToSendPrimary, options),
      this.http.post<{ [key: string]: { color: string; contrast: string } }>(url, dataToSendAccent, options)
    ]).pipe(
      tap(([primaryColors, accentColors]) => {
        const primaryPalette = this.convertToMaterialPalette(primaryColors);
        const accentPalette = this.convertToMaterialPalette(accentColors);

        // Aplicamos ambos temas (primary y accent)
        this.applyTheme(this.removeContrast(primaryPalette), '--primary');
        this.applyTheme(this.removeContrast(accentPalette), '--accent');

        // Guardamos los temas
        this.saveTheme({ primary: primaryPalette, accent: accentPalette });

        // Actualizamos el BehaviorSubject con ambos temas
        this.themeSubject.next({ primary: primaryPalette, accent: accentPalette });
      })
    );
  }

  private removeContrast(theme: any): { [key: string]: string } {
    const { contrast, ...themeWithoutContrast } = theme;
    return themeWithoutContrast;
  }

  /**
   * Convierte la estructura de la API a un formato compatible con Angular Material.
   */
  private convertToMaterialPalette(colors: { [key: string]: { color: string; contrast: string } }) {
    return {
      50: colors["50"].color,
      100: colors["100"].color,
      200: colors["200"].color,
      300: colors["300"].color,
      400: colors["400"].color,
      500: colors["500"].color,
      600: colors["600"].color,
      700: colors["700"].color,
      800: colors["800"].color,
      900: colors["900"].color,
      A100: colors["A100"].color,
      A200: colors["A200"].color,
      A400: colors["A400"].color,
      A700: colors["A700"].color,
      contrast: {
        50: colors["50"].contrast,
        100: colors["100"].contrast,
        200: colors["200"].contrast,
        300: colors["300"].contrast,
        400: colors["400"].contrast,
        500: colors["500"].contrast,
        600: colors["600"].contrast,
        700: colors["700"].contrast,
        800: colors["800"].contrast,
        900: colors["900"].contrast,
        A100: colors["A100"].contrast,
        A200: colors["A200"].contrast,
        A400: colors["A400"].contrast,
        A700: colors["A700"].contrast
      }
    };
  }

  /**
   * Aplica los colores como variables CSS globales.
   */
  private applyTheme(colors: { [key: string]: string }, prefix: string) {
    // Verificamos que 'colors' no sea nulo o indefinido
    if (colors && typeof colors === 'object') {
      Object.keys(colors).forEach((key) => {
        document.documentElement.style.setProperty(`${prefix}-${key}`, colors[key]);
      });
    } else {
      console.error('No se puede aplicar el tema. El objeto de colores es inválido.');
    }
  }

  /**
   * Guarda el tema en localStorage para persistencia.
   */
  private saveTheme(theme: any) {
    localStorage.setItem('theme-colors', JSON.stringify(theme));
  }

  /**
   * Carga el tema desde localStorage si existe.
   */
  private loadTheme() {
    const savedTheme = localStorage.getItem('theme-colors');
    if (savedTheme) {
      const theme = JSON.parse(savedTheme);

      // Verificamos si el tema tiene las paletas primary y accent
      if (theme && theme.primary && theme.accent) {
        this.themeSubject.next(theme);
        this.applyTheme(theme.primary, '--primary');
        this.applyTheme(theme.accent, '--accent');
      } else {
        console.warn('El tema guardado no tiene las paletas primarias y de acento necesarias.');
      }
    }
  }
}
